/**
 * MenyRoute — `/meny` weekly meal planner (Wave 3).
 *
 * A Monday-anchored week view of planned dinners. Meals are authored by
 * confirming a meal draft in the assistant (which also pushes missing
 * ingredients to the shopping list); this surface is read-only. ‹ › navigate
 * weeks; "Denne uka" jumps back to the current week.
 *
 * Date math is done on YYYY-MM-DD strings anchored at UTC noon so the weekday
 * labels match the stored dates regardless of the viewer's timezone (the API
 * stores Europe/Oslo calendar dates).
 *
 * #157 (redesign steg 7c, design/screens/Stell_Middag.dc.html):
 *   1. datopanelet som på Kalender: «Plan» og avataren, Plan-fanene, og uka
 *      («Uke 43» og datoene) med Denne uka og forrige/neste. Skallet legger
 *      ikke sin egen topp over Middag (`drawsOwnHeader`), og fanene står i
 *      panelet (`ownTabs`);
 *   2. én kompakt glassrad per dag: ukedag og dato til venstre, retten og
 *      ingrediensene i midten. I dag har grønn kant og «I dag» i teksten; en
 *      dag uten middag er stiplet uten glass.
 * Skissens allergen-, kokk- og handleliste-chips («Peanøtter», «Nøttefri»,
 * «Pappa lager», «2 varer på handlelista») er utelatt: dataene lagres ikke
 * (MAPPING.md). Allergivernet på Middag er fortsatt sperren i MealEditSheet.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Meal } from '@stell/shared';
import { DatePanel, EmptyState, ErrorState, Icon } from '../../components/primitives';
import { GLASS_FILL } from '../../components/primitives/Card';
import { PANEL_ROUND } from '../../components/primitives/DatePanel';
import { MeAvatarLink } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { formatWeekNumber } from '../../i18n/formatters';
import { SubTabs } from '../dashboard/SubTabsLayout';
import { useSession } from '../auth/useSession';
import { ApiError, isAdminScopeError } from '../admin/useMembers';
import { AdminReauthModal } from '../admin/AdminReauthModal';
import { useMeals, useUpdateMeal, useDeleteMeal, type MealPayload } from './useMeals';
import { MealEditSheet } from './MealEditSheet';

const weekdayFmt = new Intl.DateTimeFormat('nb-NO', { weekday: 'long', timeZone: 'UTC' });
const dayFmt = new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'long', timeZone: 'UTC' });

function todayYMD(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}
function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function mondayOf(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  const sinceMonday = (d.getUTCDay() + 6) % 7; // 0=Sun → 6, 1=Mon → 0
  return addDays(ymd, -sinceMonday);
}
function fmtWeekday(ymd: string): string {
  return weekdayFmt.format(new Date(`${ymd}T12:00:00Z`));
}
function fmtDay(ymd: string): string {
  return dayFmt.format(new Date(`${ymd}T12:00:00Z`));
}
function dayOfMonth(ymd: string): number {
  return Number(ymd.slice(8, 10));
}

/** Dagraden: glass med middag, stiplet uten; i dag med grønn kant. */
function dayRowClass(empty: boolean, isToday: boolean): string {
  const surface = empty
    ? `border-2 border-dashed ${isToday ? 'border-accent-text' : 'border-secondary-line'}`
    : `${GLASS_FILL} shadow-md ${isToday ? 'border-2 border-accent-text' : 'border border-card-line'}`;
  return `flex items-start gap-3 rounded-row py-2.5 pr-2 pl-2 ${surface}`;
}

export function MenyRoute(): ReactNode {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<string>(() => todayYMD());
  const weekStart = useMemo(() => mondayOf(anchor), [anchor]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const today = todayYMD();

  const { data, isLoading, isError, refetch } = useMeals(weekStart, weekEnd);

  // Edit/delete are admin-only (the routes are admin-scope-gated).
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  const updateMut = useUpdateMeal();
  const deleteMut = useDeleteMeal();
  const [editing, setEditing] = useState<Meal | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({ open: false, retry: null });
  const mutBusy = updateMut.isPending || deleteMut.isPending;

  async function runGuarded(fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn) });
        return;
      }
      // A meal edit that trips a household allergen → show the HARD-BLOCK.
      if (err instanceof ApiError && err.code === 'allergen_blocked') {
        setBlocked(true);
        return;
      }
      // Other errors: keep the sheet open; nothing else to surface here.
    }
  }

  function openEdit(meal: Meal): void {
    setBlocked(false);
    setEditing(meal);
  }
  function closeEdit(): void {
    setEditing(null);
    setBlocked(false);
  }
  function saveEdit(payload: MealPayload, override: boolean): void {
    if (!editing) return;
    void runGuarded(async () => {
      await updateMut.mutateAsync({ id: editing.id, payload, ...(override ? { override: true } : {}) });
      closeEdit();
    });
  }
  function deleteEdit(): void {
    if (!editing) return;
    void runGuarded(async () => {
      await deleteMut.mutateAsync(editing.id);
      closeEdit();
    });
  }

  // Bucket meals by date so each day cell renders its own.
  const byDate = useMemo(() => {
    const map = new Map<string, Meal[]>();
    for (const m of data?.meals ?? []) {
      if (!m.date) continue;
      const list = map.get(m.date);
      if (list) list.push(m);
      else map.set(m.date, [m]);
    }
    return map;
  }, [data]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const totalMeals = data?.meals.length ?? 0;

  const panel = (
    <DatePanel title={t('nav.plan')} className="md:mx-8 md:mt-4 md:rounded-2xl" action={
        // #187: tilbakemelding fra Middag, på panelets flate.
        <div className="flex shrink-0 items-center gap-2">
          <FeedbackWidget variant="icon" tone="panel" />
          <MeAvatarLink />
        </div>
      }
    >
      <SubTabs group="plan" tone="panel" className="mt-3" />

      <div className="mt-3 flex items-center gap-1">
        <div className="min-w-0 flex-1" aria-live="polite">
          <h2 data-testid="meny-week" className="m-0 truncate text-sm font-extrabold">
            {t('meny.week', { week: formatWeekNumber(new Date(`${weekStart}T12:00:00`)) })}
          </h2>
          <p data-testid="meny-week-label" className="m-0 truncate text-caption font-bold">
            {t('meny.weekLabel', { from: fmtDay(weekStart), to: fmtDay(weekEnd) })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAnchor(today)}
          data-testid="meny-this-week"
          className="flex h-11 shrink-0 items-center rounded-pill px-3.5 text-sm font-extrabold hover:bg-on-panel/10"
        >
          {t('meny.thisWeek')}
        </button>
        <button
          type="button"
          onClick={() => setAnchor(addDays(weekStart, -7))}
          aria-label={t('meny.prevWeek')}
          data-testid="meny-prev"
          className={`${PANEL_ROUND} hover:bg-on-panel/10`}
        >
          <Icon.ChevL size={20} />
        </button>
        <button
          type="button"
          onClick={() => setAnchor(addDays(weekStart, 7))}
          aria-label={t('meny.nextWeek')}
          data-testid="meny-next"
          className={`${PANEL_ROUND} hover:bg-on-panel/10`}
        >
          <Icon.ChevR size={20} />
        </button>
      </div>
    </DatePanel>
  );

  return (
    <main data-testid="meny-board" aria-label={t('nav.meal')} className="flex min-h-dvh flex-col gap-3.5 pb-6">
      {panel}

      <div className="px-5 md:px-8">
        {isError ? (
          <div data-testid="meny-error">
            <ErrorState variant="inline" heading={t('meny.loadError')} onRetry={() => void refetch()} />
          </div>
        ) : isLoading ? null : totalMeals === 0 ? (
          <EmptyState
            illustration={<Icon.Dishes size={28} />}
            heading={t('meny.empty.heading')}
            body={t('meny.empty.body')}
          />
        ) : (
          <ol className="m-0 flex list-none flex-col gap-2 p-0 md:max-w-3xl">
            {days.map((ymd, i) => {
              const meals = byDate.get(ymd) ?? [];
              const isToday = ymd === today;
              const empty = meals.length === 0;
              // «I dag» står som tekst foran metalinja, så dagen ikke bare
              // kjennes på fargen.
              const todayPrefix = isToday ? `${t('meny.today')} · ` : '';
              return (
                <li key={ymd} data-testid={`meny-day-${ymd}`} className={dayRowClass(empty, isToday)}>
                  <div aria-hidden className="w-10 shrink-0 pt-0.5 text-center">
                    <div
                      className={`text-label font-extrabold uppercase ${isToday ? 'text-accent-text' : 'text-ink-2'}`}
                    >
                      {t(`recurrence.weekdayShort.${i + 1}`)}
                    </div>
                    <div className="text-xl font-extrabold leading-tight">{dayOfMonth(ymd)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="sr-only">
                      {fmtWeekday(ymd)} {fmtDay(ymd)}
                      {isToday ? `, ${t('meny.today')}` : ''}
                    </h3>
                    {empty ? (
                      <>
                        <p className="m-0 text-body font-extrabold text-ink-2">{t('meny.noMeal')}</p>
                        <p className="m-0 text-caption text-ink-2">
                          {todayPrefix}
                          {t('meny.emptyDayHint')}
                        </p>
                      </>
                    ) : (
                      <ul className="m-0 flex list-none flex-col gap-2 p-0">
                        {meals.map((m, j) => (
                          <li key={m.id} data-testid={`meny-meal-${m.id}`} className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="m-0 text-body font-extrabold text-ink">{m.title}</p>
                              {/* Ingrediensene står i sin helhet (ikke «N ingredienser» som i
                                  skissen): middagen har ingen lagret allergenstatus, så det er
                                  her en allergen i retten kan leses. */}
                              <p className="m-0 text-caption text-ink-2">
                                {j === 0 ? todayPrefix : ''}
                                {m.ingredients.length > 0 ? m.ingredients.join(', ') : t('meny.noIngredients')}
                              </p>
                              {m.notes && <p className="m-0 text-caption italic text-ink-2">{m.notes}</p>}
                            </div>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => openEdit(m)}
                                aria-label={t('meny.edit.title')}
                                data-testid={`meny-meal-edit-${m.id}`}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-inset"
                              >
                                <Icon.ChevR size={20} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <MealEditSheet
        open={editing !== null}
        meal={editing}
        blocked={blocked}
        busy={mutBusy}
        onSave={saveEdit}
        onDelete={deleteEdit}
        onClose={closeEdit}
      />
      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={() => {
          const retry = reauth.retry;
          setReauth({ open: false, retry: null });
          retry?.();
        }}
      />
    </main>
  );
}
